
import Vue from 'vue';
import RunnitDialog from './RunnitDialog.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import {
	THUMB_SIZE,
	RUNNITS_ACCESS_LEVEL,
	RUNNIT_NODE_DEF_TAG_TYPE,
	Model,
	RunnitNodeDefTag,
	RunnitsAccessLevel,
} from '@run-diffusion/shared';
import { mapActions, mapGetters, mapState } from "vuex";
import RunnitNavTabs from "@/views/Runnits/base/RunnitNavTabs.vue";
import {
	TRAINING_TYPE_CARDS,
	RUNNITS_OWNER_SELECTION,
} from "@/views/Runnits/constants";
import EmptyState from "@/components/states/EmptyState.vue";
import { db } from "@/firebase";
import { get$bindFirestoreOptions } from "@/mixins";
import { SNACKBAR_STATUS } from "@/constants/constants";
import OfferingCard from "@/components/OfferingCard.vue";
import { ROUTER } from "@/router/constants";
import ModelTagTypeSelect from "@/views/Runnits/pages/models/ModelTagTypeSelect.vue";
import { fuzzyFilter } from '@/utils/fuzzySearch';
import { RunnitState } from '@/store';
import { TeamUserMixin } from '@/mixins';

export default Vue.extend({
	name: 'RunnitModelsDialog',
	mixins: [TeamUserMixin],
	props: {
		value: { type: Boolean, default: false },
		maxWidth: { type: [Number, String], default: '1100px' },
		persistent: { type: Boolean, default: false },
		modelArchitecture: { type: String, required: true },
	},
	data () {
		return {
			RUNNIT_NODE_DEF_TAG_TYPE,
			RUNNITS_ACCESS_LEVEL,
			THUMB_SIZE,
			TRAINING_TYPE_CARDS,
			open: false,
			currNavTab: RUNNITS_ACCESS_LEVEL.PRIVATE,

			loadingModels: false,
			models: [],

			modelSearchValue: '',
			filtersCollapsed: false,

			isAllCategorySelected: true,
			selectedTypes: [],
		};
	},
	created () {
		this.open = !!this.value;
	},
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
			'modelsState',
			'publicRunnitNodeDefTagsMap',
		]),
		...mapGetters([
			'displayLabels',
		]),
		modelsNavTabs () {
			const hasTeam: boolean = this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && this.team?.id;

			if (hasTeam) {
				return [
					{
						id: RUNNITS_ACCESS_LEVEL.PRIVATE,
						label: `${this.displayLabels.team} Private Models`,
						icon: 'mdi-weight-lifter',
					},
					{
						id: RUNNITS_ACCESS_LEVEL.SHARED,
						label: `${this.displayLabels.team} Shared Models`,
						icon: 'mdi-account-group-outline',
					},
				];
			} else {
				return [
					{
						id: RUNNITS_ACCESS_LEVEL.PRIVATE,
						label: 'Personal Models',
						icon: 'mdi-weight-lifter',
					},
				];
			}
		},
		filteredModels () {
			return this.models.filter((model: Model) => {
				// Filter by model architecture if specified
				if (this.modelArchitecture && model.architecture !== this.modelArchitecture) {
					return false;
				}

				// Filter out non done models
				if (!model.file) {
					return false;
				}

				// Type filter
				const matchesType = this.isAllCategorySelected || 
					!this.selectedTypes?.length ||
					this.selectedTypes.some(type => model.tags?.[type]);
				if (!matchesType) return false;

				// Filter by search text
				if (!this.modelSearchValue) {
					return true;
				}
				return fuzzyFilter(
					[model],
					this.modelSearchValue,
					[
						{ name: 'name', weight: 0.7 },
						{ name: 'description', weight: 0.3 }
					]
				).length > 0;
			});
		},
		qualityTags () {
			return this.filteredModels
				.map((model: Model) => model.tags)
				.map((tags: Record<string, RunnitNodeDefTag>) => Object.keys(tags))
				.map((tagIds: string[]) => tagIds.map((tagId: string) => this.publicRunnitNodeDefTagsMap[tagId]))
				.flat()
				.filter((tag: RunnitNodeDefTag) => tag && tag.type === RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_QUALITY);
		},
	},
	watch: {
		value (newVal: boolean) {
			this.setOpen(newVal);
		},
		runnitState: {
			immediate: true,
			async handler (newVal: RunnitState, oldVal: RunnitState) {
				let newCurrNavTabId: RunnitsAccessLevel = newVal.runnitsAccessLevel;
				if (
					newCurrNavTabId === RUNNITS_ACCESS_LEVEL.SHARED &&
					!(
						newVal.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
						this.team
					)
				) {
					newCurrNavTabId = RUNNITS_ACCESS_LEVEL.PRIVATE;
				}
				if (newCurrNavTabId !== this.currNavTab) {
					this.changeNavTab(newCurrNavTabId);
				}
			}
		},
		currNavTab: {
			immediate: true,
			async handler (newTabId: RunnitsAccessLevel) {
				try {
					this.loadingModels = true;
					if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && this.team && this.user) {
						// index created: models - teamId Ascending accessLevel Ascending isDeleted Ascending createdAt Descending __name__ Descending
						let modelsRef: any = db.collection(`models`)
							.where('teamId', '==', this.team.id)
							.where('accessLevel', '==', newTabId)
							.where('isDeleted', '==', false)
							.orderBy('createdAt', 'desc')
							.limit(1000);
						if (newTabId !== RUNNITS_ACCESS_LEVEL.SHARED) {
							modelsRef = modelsRef.where('userId', '==', this.user.id);
						}
						await this.$bind(
							'models',
							modelsRef,
							get$bindFirestoreOptions(),
						);
					} else if (this.user) {
						// index created: models - userId Ascending teamId Ascending accessLevel Ascending isDeleted Ascending createdAt Descending __name__ Descending
						const modelsRef: any = db.collection(`models`)
							.where('userId', '==', this.user.id)
							.where('teamId', '==', null)
							.where('accessLevel', '==', newTabId)
							.where('isDeleted', '==', false)
							.orderBy('createdAt', 'desc')
							.limit(100);
						await this.$bind(
							'models',
							modelsRef,
							get$bindFirestoreOptions(),
						);
					}
				} catch (e) {
					console.error(e);
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'Error loading models',
						show: true,
					});
				} finally {
					this.loadingModels = false;
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateModelState',
			'updateSnackbar',
		]),
		setOpen (val: boolean) {
			this.open = !!val;
			if (this.open !== this.value) {
				this.$emit('input', this.open);
			}
		},
		onCancel () {
			this.setOpen(false);
		},
		canEditModel (model: Model) {
			return !!(
				model.userId === this.user.id ||
				(
					model.accessLevel === RUNNITS_ACCESS_LEVEL.SHARED &&
					this.hasShareRunnitAssetsPermission
				)
			);
		},
		onModelSelect (model: Model) {
			this.$emit('on-model-select', model);
			this.setOpen(false);
		},
		changeNavTab (newTabId: RunnitsAccessLevel) {
			this.currNavTab = newTabId;
			this.$emit('on-nav-tab-change', newTabId);
		},
		onEditModelSettings (model: Model) {
			if (model) {
				this.updateModelState({
					modelDraft: model,
					modelDialog: {
						...this.modelsState.modelDialog,
						open: true,
						mode: 'edit',
					},
				});
			}
		},
		goToModelsPage () {
			this.setOpen(false);
			this.routerPush(this.$route, this.$router, {
				name: ROUTER.RUNNITS_MODELS_LIST,
			});
		},
		handleSearchInput (searchValue: string) {
			this.modelSearchValue = searchValue;
		},
		toggleFilterCollapse () {
			this.filtersCollapsed = !this.filtersCollapsed;
		},
		handleMultiSelectFilter (tagIds: string[]) {
			this.isAllCategorySelected = false;
			this.selectedTypes = tagIds;
		},
		handleAllSelectFilter () {
			this.isAllCategorySelected = true;
			this.selectedTypes = [];
		},
	},
	components: {
		ModelTagTypeSelect,
		OfferingCard,
		EmptyState,
		RunnitNavTabs,
		RunnitDialog,
		LoadingState,
	},
});
